import { useState } from "react";
import axios from "axios";

/**
 * Custom hook for verifying reCAPTCHA Enterprise tokens with backend
 * 
 * @returns {Object} Hook state and methods
 * @property {boolean} loading - Whether verification is in progress
 * @property {boolean} success - Whether verification succeeded
 * @property {string|null} error - Any error that occurred during verification
 * @property {Function} verifyRecaptcha - Function to verify a reCAPTCHA token with expected action
 */
function useVerifyRecaptcha() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Verifies a reCAPTCHA token with the backend
   * @param {string} recaptchaToken - The token generated by reCAPTCHA Enterprise
   * @param {string} expectedAction - The expected action name for validation
   * @returns {Promise<boolean>} True if verification succeeds, false otherwise
   */
  const verifyRecaptcha = async (recaptchaToken, expectedAction) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.post(
        "https://62gl3yhjx6.execute-api.us-east-1.amazonaws.com/api-request/verifyRecaptcha",
        {
          recaptchaToken,
          expectedAction,
        }
      );
      
      if (response.status === 200) {
        setLoading(false);
        setSuccess(true);
        return true;
      } else if (response.status === 400) {
        setLoading(false);
        setError("reCAPTCHA verification failed. Please try again.");
        return false;
      }
    } catch (err) {
      setLoading(false);
      setError("An error occurred while verifying reCAPTCHA.");
      console.error("Error verifying reCAPTCHA:", err);
      return false;
    }
  };

  return { verifyRecaptcha, error, loading, success };
}

export default useVerifyRecaptcha;
